import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  FileText,
  KeyRound,
  LogOut,
  Settings,
  Shield,
  Trash2,
  Users,
} from "lucide-react";
import ProfileSectionCard from "./ProfileSectionCard";
import CloseAccountModal from "./CloseAccountModal";
import ChangePasswordModal from "./ChangePasswordModal";
import LogoutConfirmModal from "./LogoutConfirmModal";
import WebPushSettingsPanel from "./WebPushSettingsPanel";
import { logoutAppUser } from "../../redux/appAuthSlice";
import { PROFILE_SETTINGS_LINK_ROWS } from "../../utils/profileHelpers";
import { HOME_PATH } from "../../config/products";

const APP_HOME = HOME_PATH;

const settingIcons = {
  privacy: Shield,
  terms: FileText,
  community: Users,
  password: KeyRound,
};

const ProfileSettingsPanel = ({ userId, onNotify }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [closeAccountOpen, setCloseAccountOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);

  const confirmLogout = () => {
    setLogoutOpen(false);
    dispatch(logoutAppUser());
    navigate(APP_HOME);
  };

  return (
    <>
      <WebPushSettingsPanel />

      <ProfileSectionCard id="settings" eyebrow="Account" title="Settings" icon={Settings}>
        <div className="prof-settings-group">
          <ul className="prof-settings-list prof-settings-list-grouped">
            {PROFILE_SETTINGS_LINK_ROWS.map((row) => {
              const Icon = settingIcons[row.id] || Settings;
              const isLink = Boolean(row.path);
              const isPassword = row.id === "password";
              const Tag = isLink || isPassword ? "button" : "div";

              const handleClick = () => {
                if (row.path) navigate(row.path);
                if (isPassword) setPasswordOpen(true);
              };

              return (
                <li key={row.id}>
                  <Tag
                    type={isLink || isPassword ? "button" : undefined}
                    className={`prof-settings-row${isLink || isPassword ? " prof-settings-row-link" : ""}`}
                    onClick={isLink || isPassword ? handleClick : undefined}
                  >
                    <span className="prof-settings-icon prof-settings-icon-link" aria-hidden>
                      <Icon size={13} />
                    </span>
                    <div className="prof-settings-copy">
                      <strong>{row.label}</strong>
                      <span>{row.description}</span>
                    </div>
                  </Tag>
                </li>
              );
            })}
          </ul>
        </div>

        <button
          type="button"
          className="prof-logout-btn"
          onClick={() => setLogoutOpen(true)}
        >
          <LogOut size={14} aria-hidden />
          Log out of BetBond
        </button>
      </ProfileSectionCard>

      <ProfileSectionCard
        id="close-account"
        className="prof-section-danger"
        eyebrow="Danger zone"
        title="Delete your account"
        icon={AlertTriangle}
      >
        <p className="prof-danger-hint">
          If every check passes, deletion happens immediately in the app. Clear wallet
          balances and finish open activity in House, Markets, Bond Play, and Bond Draw first.
        </p>
        <button
          type="button"
          className="prof-danger-btn"
          onClick={() => setCloseAccountOpen(true)}
        >
          <Trash2 size={13} aria-hidden />
          Delete account
        </button>
      </ProfileSectionCard>

      <ChangePasswordModal
        open={passwordOpen}
        onClose={() => setPasswordOpen(false)}
        userId={userId}
        onNotify={onNotify}
      />

      <LogoutConfirmModal
        open={logoutOpen}
        onClose={() => setLogoutOpen(false)}
        onConfirm={confirmLogout}
      />

      <CloseAccountModal
        open={closeAccountOpen}
        onClose={() => setCloseAccountOpen(false)}
        onNotify={onNotify}
      />
    </>
  );
};

export default ProfileSettingsPanel;
